import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import JSZip from "jszip";
import toast from "react-hot-toast";
import { VApplication } from "../application";
import { DOWNLOAD_CREDENTIAL_ZIP_V2 } from "../services/IssuedCredentialPageService/graphql";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";

export type DownloadPhase = "IDLE" | "PROCESSING" | "DOWNLOADING" | "ZIPPING" | "COMPLETED" | "ERROR";

interface DownloadState {
  phase: DownloadPhase;
  completed: number;
  total: number;
  status: string;
}

interface DownloadContextType {
  state: DownloadState;
  startDownload: (input: any) => Promise<void>;
  resetDownload: () => void;
}

const DownloadContext = createContext<DownloadContextType | undefined>(undefined);

export const DownloadProvider: React.FC = ({ children }) => {
  const [state, setState] = useState<DownloadState>({
    phase: "IDLE",
    completed: 0,
    total: 0,
    status: "",
  });

  const downloadTimerRef = useRef<number | null>(null);

  const resetDownload = useCallback(() => {
    setState({
      phase: "IDLE",
      completed: 0,
      total: 0,
      status: "",
    });
  }, []);

  const startDownload = useCallback(async (input: any) => {
    if (["PROCESSING", "DOWNLOADING", "ZIPPING"].includes(state.phase)) {
      return;
    }
    const app = VApplication.getInstance();
    const graphqlModule = app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
    const client = graphqlModule?.getClient("graphql-gateway");

    if (!client) {
      toast.error("Network error: Download client unavailable");
      return;
    }

    setState({ phase: "PROCESSING", completed: 0, total: 0, status: "Preparing links..." });
    
    const startedAt = Date.now();
    if (downloadTimerRef.current) window.clearInterval(downloadTimerRef.current);
    
    downloadTimerRef.current = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      if (elapsed >= 5) {
        setState(prev => ({ 
          ...prev, 
          status: `Generating URLs... (${elapsed}s). This may take a moment for large batches.` 
        }));
      }
    }, 2000);

    try {
      const res = await client.mutate({
        mutation: DOWNLOAD_CREDENTIAL_ZIP_V2,
        variables: { input },
        fetchPolicy: "no-cache",
      });

      if (downloadTimerRef.current) window.clearInterval(downloadTimerRef.current);

      const payload = res?.data?.downloadCredentialZipV2;
      const items = payload?.presignedUrls || [];

      if (items.length === 0) {
        toast.error("No certificates found for this selection.");
        setState({ phase: "IDLE", completed: 0, total: 0, status: "" });
        return;
      }

      const total = items.length;
      setState({ phase: "DOWNLOADING", completed: 0, total, status: `Downloading certificates (0/${total})...` });

      // Convert presigned S3 URLs to direct regional public URLs.
      // The bucket uses ACL: public-read, so we don't need the signature.
      // This matches the URL format used by the View Certificate page
      // (s3.ap-south-1.amazonaws.com) which works without auth params.
      const fixS3Url = (url: string): string => {
        try {
          const parsed = new URL(url);
          const isS3 =
            parsed.hostname === "s3.amazonaws.com" ||
            parsed.hostname.includes(".s3.amazonaws.com") ||
            parsed.hostname.includes(".s3.ap-south-1.amazonaws.com") ||
            parsed.hostname === "s3.ap-south-1.amazonaws.com";

          if (!isS3) return url;

          // Force the regional hostname
          if (parsed.hostname === "s3.amazonaws.com") {
            parsed.hostname = "s3.ap-south-1.amazonaws.com";
          } else if (parsed.hostname.endsWith(".s3.amazonaws.com")) {
            parsed.hostname = parsed.hostname.replace(
              ".s3.amazonaws.com",
              ".s3.ap-south-1.amazonaws.com"
            );
          }

          // Strip presigned auth params — files are publicly readable
          parsed.searchParams.delete("AWSAccessKeyId");
          parsed.searchParams.delete("Expires");
          parsed.searchParams.delete("Signature");
          parsed.searchParams.delete("X-Amz-Algorithm");
          parsed.searchParams.delete("X-Amz-Credential");
          parsed.searchParams.delete("X-Amz-Date");
          parsed.searchParams.delete("X-Amz-Expires");
          parsed.searchParams.delete("X-Amz-SignedHeaders");
          parsed.searchParams.delete("X-Amz-Signature");
          parsed.searchParams.delete("X-Amz-Security-Token");

          return parsed.toString();
        } catch { /* return original on parse error */ }
        return url;
      };

      const zip = new JSZip();
      const batchSize = 10;
      let completedCount = 0;

      for (let i = 0; i < total; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (item: any) => {
            try {
              if (item.url) {
                const response = await fetch(fixS3Url(item.url));
                if (!response.ok) throw new Error("Fetch failed");
                const blob = await response.blob();
                zip.file(item.fileName, blob);
              } else if (item.base64) {
                zip.file(item.fileName, item.base64, { base64: true });
              }
            } catch (err) {
              console.error(`Failed to download ${item.fileName}:`, err);
            } finally {
              completedCount += 1;
              setState(prev => ({ 
                ...prev, 
                completed: completedCount,
                status: `Downloading certificates (${completedCount}/${total})...` 
              }));
            }
          })
        );
      }

      setState(prev => ({ ...prev, phase: "ZIPPING", status: "Bundling certificates..." }));
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const fileName = payload?.fileName || "certificates.zip";

      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success("Download complete: All certificates bundled successfully!");
      setState({ phase: "COMPLETED", completed: total, total, status: "Done!" });
      
      // Clear after 3 seconds
      setTimeout(() => {
        setState(prev => prev.phase === "COMPLETED" ? { ...prev, phase: "IDLE" } : prev);
      }, 3000);

    } catch (err: any) {
      if (downloadTimerRef.current) window.clearInterval(downloadTimerRef.current);
      console.error("Bulk download failed:", err);
      toast.error("failed to complete bulk download");
      setState({ phase: "ERROR", completed: 0, total: 0, status: "Error occurred" });
    }
  }, []);

  return (
    <DownloadContext.Provider value={{ state, startDownload, resetDownload }}>
      {children}
    </DownloadContext.Provider>
  );
};

export const useDownloadContext = () => {
  const context = useContext(DownloadContext);
  if (!context) {
    throw new Error("useDownloadContext must be used within a DownloadProvider");
  }
  return context;
};
